import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPE_LABELS,
  ASSET_TYPES,
  CONTAINER_KIND_LABELS,
  CONTAINER_KINDS,
  isExceptionStatus,
  roleCanEditCatalog,
  roleCanManageReplacements,
  roleCanMoveAssets,
  type AssetMoveListResponse,
  type AssetMoveResponse,
  type AssetResponse,
  type AssetStatus,
  type AssetType,
  type BinListResponse,
  type ContainerKind,
  type PublicAsset,
  type PublicAssetMove,
  type PublicAssetPhoto,
  type PublicBin,
  type PublicTote,
  type PublicTrailer,
  type ToteListResponse,
  type TrailerListResponse,
} from '@inventohub/shared';
import { ExceptionBanner } from '../components/ExceptionBanner';
import { ApiClientError, apiDownload, apiRequest, triggerDownload } from '../lib/api';
import { useAuth } from '../lib/auth';
import { readImageAsBase64 } from '../lib/files';

export function AssetDetailPage({
  qrLanding = false,
  serial: serialProp,
}: {
  qrLanding?: boolean;
  serial?: string;
}): JSX.Element {
  const { user } = useAuth();
  const params = useParams();
  const serial = serialProp ?? params.serial ?? '';
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const canMove = user ? roleCanMoveAssets(user.role) : false;
  const [asset, setAsset] = useState<PublicAsset | null>(null);
  const [photos, setPhotos] = useState<PublicAssetPhoto[]>([]);
  const [moves, setMoves] = useState<PublicAssetMove[]>([]);
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [bins, setBins] = useState<PublicBin[]>([]);
  const [trailers, setTrailers] = useState<PublicTrailer[]>([]);
  const [destKind, setDestKind] = useState<ContainerKind>('uncontained');
  const [destId, setDestId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<AssetResponse>(`/api/assets/${encodeURIComponent(serial)}`);
        if (cancelled) return;
        setAsset(body.asset);
        setPhotos(body.photos);
        setDestKind(body.asset.containerKind);
        setDestId(body.asset.containerId ?? '');
        const extra = await Promise.all([
          apiRequest<AssetMoveListResponse>(`/api/assets/${body.asset.id}/moves`),
          canMove
            ? apiRequest<ToteListResponse>('/api/totes')
            : Promise.resolve({ totes: [] as PublicTote[] }),
          canMove
            ? apiRequest<BinListResponse>('/api/bins')
            : Promise.resolve({ bins: [] as PublicBin[] }),
          canMove
            ? apiRequest<TrailerListResponse>('/api/trailers')
            : Promise.resolve({ trailers: [] as PublicTrailer[] }),
        ]);
        if (cancelled) return;
        setMoves(extra[0].moves);
        setTotes(extra[1].totes);
        setBins(extra[2].bins);
        setTrailers(extra[3].trailers);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load this asset.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serial, canMove]);

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!asset) return;
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<AssetResponse>(`/api/assets/${asset.id}`, {
        method: 'PATCH',
        body: {
          type: asset.type,
          status: asset.status,
          notes: asset.notes,
          label: asset.label,
          purchaseDate: asset.purchaseDate,
          replacementCost: asset.replacementCost,
          warrantyExpiry: asset.warrantyExpiry,
          firmware: asset.firmware,
          lastServiceDate: asset.lastServiceDate,
        },
      });
      setAsset(body.asset);
      setPhotos(body.photos);
      setNotice('Saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save the asset.');
    }
  }

  async function onMove(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!asset) return;
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<AssetMoveResponse>(`/api/assets/${asset.id}/moves`, {
        method: 'POST',
        body: {
          containerKind: destKind,
          ...(destKind === 'uncontained' ? {} : { containerId: destId }),
          reason: reason || null,
        },
      });
      setAsset(body.asset);
      setMoves((current) => [body.move, ...current.filter((entry) => entry.id !== body.move.id)]);
      setReason('');
      setNotice(
        body.asset.containerName
          ? `Moved to ${body.asset.containerName}. Membership is permanent until the next shop move.`
          : 'Moved out of its container. Membership is permanent until the next shop move.',
      );
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not move the asset.');
    }
  }

  async function onPhoto(file: File | undefined): Promise<void> {
    if (!asset || !file) return;
    setError(null);
    try {
      const payload = await readImageAsBase64(file);
      const body = await apiRequest<{ photo: PublicAssetPhoto }>(`/api/assets/${asset.id}/photos`, {
        method: 'POST',
        body: payload,
      });
      setPhotos((current) => [...current, body.photo]);
      setNotice('Photo added.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not add the photo.');
    }
  }

  async function printLabel(): Promise<void> {
    if (!asset) return;
    try {
      const { blob, filename } = await apiDownload('/api/labels', {
        method: 'POST',
        body: { assetSerials: [asset.serial] },
      });
      triggerDownload(blob, filename);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not print the label.');
    }
  }

  const destinationOptions =
    destKind === 'tote'
      ? totes.map((tote) => ({ id: tote.id, label: `Tote ${tote.number}` }))
      : destKind === 'bin'
        ? bins.map((bin) => ({ id: bin.id, label: bin.name }))
        : destKind === 'trailer'
          ? trailers.map((trailer) => ({ id: trailer.id, label: trailer.name }))
          : [];

  const destinationUnchanged =
    asset !== null &&
    destKind === asset.containerKind &&
    (destKind === 'uncontained' || destId === (asset.containerId ?? ''));

  return (
    <section data-testid={qrLanding ? 'qr-landing' : 'asset-detail'}>
      <div className="card">
        <h2>Asset {asset?.serial ?? serial}</h2>
        {qrLanding ? (
          <p data-testid="qr-signed-in">
            Signed in as {user?.name}. You were not asked to log in again.
          </p>
        ) : null}
        {loading ? <p className="meta">Loading…</p> : null}
        {error ? (
          <p className="status status--error" data-testid="asset-detail-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="asset-detail-notice">
            {notice}
          </p>
        ) : null}
        {asset ? (
          <>
            <p className="meta">
              {ASSET_TYPE_LABELS[asset.type]} · {ASSET_STATUS_LABELS[asset.status]}
            </p>
            <ExceptionBanner status={asset.status} label={`Asset ${asset.serial}`} />
            {isExceptionStatus(asset.status) && user && roleCanManageReplacements(user.role) ? (
              <Link
                className="button button--ghost"
                to={`/replacements/new?kind=${asset.status}&assetId=${asset.id}${asset.containerKind === 'tote' && asset.containerId ? `&toteId=${asset.containerId}` : ''}`}
                data-testid="request-replacement"
              >
                Request replacement
              </Link>
            ) : null}
            {asset.toteNumber ? (
              <p>
                <Link
                  to={`/totes/${encodeURIComponent(asset.toteNumber)}`}
                  data-testid="asset-tote-link"
                >
                  Tote {asset.toteNumber} inventory
                </Link>
              </p>
            ) : (
              <p className="meta" data-testid="asset-container">
                {asset.containerName
                  ? `${CONTAINER_KIND_LABELS[asset.containerKind]}: ${asset.containerName}`
                  : 'Not in a tote.'}
              </p>
            )}
            {canEdit ? (
              <form onSubmit={(event) => void onSave(event)}>
                <label className="field">
                  Type
                  <select
                    value={asset.type}
                    onChange={(event) =>
                      setAsset({ ...asset, type: event.target.value as AssetType })
                    }
                  >
                    {ASSET_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {ASSET_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  Status
                  <select
                    value={asset.status}
                    onChange={(event) =>
                      setAsset({ ...asset, status: event.target.value as AssetStatus })
                    }
                  >
                    {ASSET_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {ASSET_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  Label
                  <input
                    value={asset.label ?? ''}
                    onChange={(event) => setAsset({ ...asset, label: event.target.value || null })}
                  />
                </label>
                <label className="field">
                  Purchase date
                  <input
                    type="date"
                    value={asset.purchaseDate ?? ''}
                    onChange={(event) =>
                      setAsset({ ...asset, purchaseDate: event.target.value || null })
                    }
                  />
                </label>
                <label className="field">
                  Replacement cost
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={asset.replacementCost ?? ''}
                    onChange={(event) =>
                      setAsset({
                        ...asset,
                        replacementCost:
                          event.target.value === '' ? null : Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label className="field">
                  Warranty expiry
                  <input
                    type="date"
                    value={asset.warrantyExpiry ?? ''}
                    onChange={(event) =>
                      setAsset({ ...asset, warrantyExpiry: event.target.value || null })
                    }
                  />
                </label>
                <label className="field">
                  Firmware
                  <input
                    value={asset.firmware ?? ''}
                    onChange={(event) =>
                      setAsset({ ...asset, firmware: event.target.value || null })
                    }
                  />
                </label>
                <label className="field">
                  Last service
                  <input
                    type="date"
                    value={asset.lastServiceDate ?? ''}
                    onChange={(event) =>
                      setAsset({ ...asset, lastServiceDate: event.target.value || null })
                    }
                  />
                </label>
                <label className="field">
                  Notes
                  <textarea
                    value={asset.notes ?? ''}
                    onChange={(event) => setAsset({ ...asset, notes: event.target.value || null })}
                  />
                </label>
                <button type="submit" className="button">
                  Save asset
                </button>
              </form>
            ) : (
              <p className="meta">{asset.notes}</p>
            )}
            {canEdit ? (
              <>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => void printLabel()}
                >
                  Print QR label
                </button>
                <label className="field">
                  Add photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="asset-photo"
                    onChange={(event) => void onPhoto(event.target.files?.[0])}
                  />
                </label>
              </>
            ) : null}
            <div className="photo-grid">
              {photos.map((photo) => (
                <img
                  key={photo.id}
                  src={`/api/assets/${asset.id}/photos/${photo.id}`}
                  alt={`${asset.serial} photo`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {canMove && asset ? (
        <form className="card" onSubmit={(event) => void onMove(event)} data-testid="asset-move">
          <h2>Shop move</h2>
          <p className="meta">
            Official membership. A substitute placed into a tote stays there until it is moved again
            — there is no trip-only membership.
          </p>
          <label className="field">
            Destination
            <select
              value={destKind}
              onChange={(event) => {
                setDestKind(event.target.value as ContainerKind);
                setDestId('');
              }}
              data-testid="asset-move-kind"
            >
              {CONTAINER_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {CONTAINER_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </label>
          {destKind !== 'uncontained' ? (
            <label className="field">
              Place in
              <select
                value={destId}
                required
                onChange={(event) => setDestId(event.target.value)}
                data-testid="asset-move-container"
              >
                <option value="">Select…</option>
                {destinationOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            Reason (optional)
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              data-testid="asset-move-reason"
            />
          </label>
          <button
            type="submit"
            className="button"
            data-testid="asset-move-submit"
            disabled={destinationUnchanged || (destKind !== 'uncontained' && destId === '')}
          >
            Record move
          </button>
        </form>
      ) : null}

      {asset ? (
        <div className="card" data-testid="asset-move-history">
          <h2>Move history</h2>
          {moves.length === 0 ? (
            <p className="meta">No shop moves recorded yet.</p>
          ) : (
            moves.map((move) => (
              <p key={move.id} className="meta" data-testid={`asset-move-row-${move.id}`}>
                {formatMoveLine(move)}
              </p>
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}

function formatMoveLine(move: PublicAssetMove): string {
  const from = move.fromName
    ? `${CONTAINER_KIND_LABELS[move.fromKind]} ${move.fromName}`
    : CONTAINER_KIND_LABELS[move.fromKind];
  const to = move.toName
    ? `${CONTAINER_KIND_LABELS[move.toKind]} ${move.toName}`
    : CONTAINER_KIND_LABELS[move.toKind];
  const who = move.movedByName ?? 'Unknown';
  const why = move.reason ? ` — ${move.reason}` : '';
  return `${from} → ${to} · ${who}${why}`;
}
